import { DateTime } from 'luxon';

import { type ChartOfAccountProps, CoaTypeEnum } from 'app-components/flux/chart-of-accounts/chartOfAccountsTypes';
import { formatSplitItemName } from 'app-components/flux/transactions/splitsHelpers';
import type { SplitItemProps, TransactionProps } from 'app-components/flux/transactions/transactionsTypes';

import {
  isClassifiedMerchantOrder,
  type MerchantOrder,
  type MerchantOrderCharge,
  type MerchantOrderItem,
  type MerchantOrderSkipReason,
  merchantOrderSkipReason,
} from './merchantOrdersTypes';

export const UNCATEGORIZED_COA: ChartOfAccountProps = { id: '0', type: CoaTypeEnum.UNCATEGORIZED };

export type BuildOrderCandidateOptions = {
  /**
   * Category for the `Tax` row. Resolved per dataset by the caller, since the id is dataset-scoped.
   * Absent leaves the row uncategorised, which is the honest answer when we cannot resolve it.
   */
  salesTaxCoa?: ChartOfAccountProps | null;
};

/** Label for the row that absorbs whatever the itemised rows failed to account for. */
export const RESIDUAL_ROW_NAME = 'Other';

/**
 * Structurally identical to the exported `ReceiptSplitItem`, deliberately, so the two builders can
 * be unified later without touching call sites.
 */
export type OrderSplitItem = Pick<SplitItemProps, 'amount' | 'name'> & {
  coa: ChartOfAccountProps;
};

/**
 * What an order says about the transaction it belongs to.
 */
export type CandidateTransaction = Pick<TransactionProps, 'amount' | 'postedOn'> & {
  coa: ChartOfAccountProps | null;
  /** The item name, on a categorized transaction only. Split rows carry their own names. */
  memo?: string;
  split: { items: OrderSplitItem[] } | null;
};

export type OrderCandidate = {
  order: MerchantOrder;
  transaction: CandidateTransaction;
  /** Last four digits of the paying card, when exactly one card paid. Scopes the register search. */
  accountHint?: string;
};

/** A candidate whose transaction is a split, as opposed to a plain categorisation. */
export type SplitOrderCandidate = OrderCandidate & {
  transaction: CandidateTransaction & { split: { items: OrderSplitItem[] } };
};

/**
 * Which of the two shapes `toCandidateTransaction` produced. Callers write a split one way and a
 * categorized one another, so the distinction has to survive out of this module.
 */
export const isSplitCandidate = (candidate: OrderCandidate): candidate is SplitOrderCandidate =>
  candidate.transaction.split !== null;

export type BuildOrderCandidateResult =
  | { ok: true; candidate: OrderCandidate }
  | { ok: false; skipReason: MerchantOrderSkipReason };

const toCents = (value: number): number => Math.round(value * 100);
const fromCents = (cents: number): number => cents / 100;

const asCents = (value: number | null | undefined): number | undefined =>
  value == null || !Number.isFinite(value) ? undefined : toCents(value);

/**
 * Orders state money as positive spend — a $295 order reports `295.00`. Simplifi transactions and
 * their split rows are signed from the transaction's point of view, so a purchase is negative.
 *
 * Every amount crosses that boundary in exactly two places: here for the order total, and in
 * `toRow` for each individual row. Nothing else in this file negates, so a new row type cannot
 * pick up the wrong sign.
 */
const toTransactionCents = (orderCents: number): number => -orderCents;

/** Money rounded to the nearest cent, or undefined when there is no usable number. */
const roundNearestCent = (value: number | null | undefined): number | undefined => {
  const cents = asCents(value);
  return cents === undefined ? undefined : fromCents(cents);
};

/**
 * Builds one split row, negating the order's positive amount into transaction sign (see
 * `toTransactionCents`). Every row type routes through here — items, tax, shipping, charges — so
 * this is the only place a row's sign is decided. Zero rows are dropped, and a negative adjustment
 * such as a promotion flips into a positive row that offsets the items.
 */
const toRow = (
  name: string | null | undefined,
  value: number | null | undefined,
  coa: ChartOfAccountProps,
): OrderSplitItem | undefined => {
  const amount = roundNearestCent(value);
  const rowName = formatSplitItemName(name);
  if (!amount || !rowName) {
    return undefined;
  }

  return { amount: -amount, coa, name: rowName };
};

const itemRowName = (item: MerchantOrderItem): string => {
  const quantity = Number(item.quantity);
  return Number.isFinite(quantity) && quantity > 1 ? `${item.name} (Qty ${quantity})` : item.name;
};

const itemRow = (item: MerchantOrderItem): OrderSplitItem | undefined =>
  toRow(itemRowName(item), item.amount, item.coa ?? UNCATEGORIZED_COA);

const compact = (rows: (OrderSplitItem | undefined)[]): OrderSplitItem[] =>
  rows.filter((row): row is OrderSplitItem => row !== undefined);

const namedRows = (charges: MerchantOrderCharge[] | null | undefined, fallbackName: string) =>
  (charges ?? []).map((charge) => toRow(charge.name ?? fallbackName, charge.amount, UNCATEGORIZED_COA));

/**
 * Item rows, then every order-level amount as its own row, then a residual row so the split is
 * guaranteed to sum to the grand total. No proportional allocation — tax and shipping stay visible
 * as their own lines rather than being smeared across the items.
 */
export const buildOrderSplitItems = (
  order: MerchantOrder,
  transactionCents: number,
  options: BuildOrderCandidateOptions = {},
): OrderSplitItem[] => {
  const itemRows = compact((order.items ?? []).map(itemRow));
  // An order whose items did not survive scraping still splits against its product total.
  const baseRows = itemRows.length > 0 ? itemRows : [toRow('Items', order.totalAmountForProducts, UNCATEGORIZED_COA)];

  const rows = compact([
    ...baseRows,
    toRow('Shipping', order.shippingFee, UNCATEGORIZED_COA),
    toRow('Delivery', order.deliveryFee, UNCATEGORIZED_COA),
    ...namedRows(order.additionalCharges, 'Additional charge'),
    ...namedRows(order.adjustmentsAmount, 'Adjustment'),
    toRow('Tax', order.tax, options.salesTaxCoa ?? UNCATEGORIZED_COA),
    toRow('Tip', order.tip, UNCATEGORIZED_COA),
    // Arrives negative, so the shared negation in `toRow` turns it into the positive offset it is:
    // money that funded the order without leaving this account. Not spend, so it stays
    // uncategorised — where the balance came from is not something the order can tell us.
    toRow('Gift card', order.giftCardAmount, UNCATEGORIZED_COA),
  ]);

  if (rows.length === 0) {
    return rows;
  }

  const residualCents = transactionCents - rows.reduce((sum, row) => sum + toCents(row.amount), 0);
  if (residualCents !== 0) {
    rows.push({ amount: fromCents(residualCents), coa: UNCATEGORIZED_COA, name: RESIDUAL_ROW_NAME });
  }

  return rows;
};

const lastFourOf = (value: number | string | null | undefined): string | undefined => {
  if (value == null) {
    return undefined;
  }
  const digits = String(value).replace(/\D/g, '');
  return digits ? digits.slice(-4).padStart(4, '0') : undefined;
};

/**
 * Only a single payment row yields a hint. Deduplicating card suffixes instead would hint on an
 * order charged twice to the same card, and scoping is the one lever that can turn a skip into a
 * match — so more than one row means no hint, even when the account is unambiguous.
 */
const accountHintFor = (order: MerchantOrder): string | undefined => {
  const payments = order.paymentInfo ?? [];
  return payments.length === 1 ? lastFourOf(payments[0].lastFourDigit) : undefined;
};

/** `orderDate` is already a calendar date; this guards against a timestamp creeping in. */
export const toPostedOnDate = (isoDate: string): string =>
  DateTime.fromISO(isoDate, { setZone: true }).toISODate() ?? isoDate;

/**
 * One row is a categorisation, not a split — a split needs two sides to be worth calling one.
 *
 * `preProcessUpdate` would collapse a one-row split into exactly this on the way to the server, but
 * relying on that would make the outcome depend on a safety net staying in place. Stating it here
 * means the category and the item name land whatever the caller does with the result, and the
 * candidate describes the transaction we actually want rather than one shape upstream of it.
 */
const toCandidateTransaction = (
  order: MerchantOrder,
  transactionCents: number,
  items: OrderSplitItem[],
): CandidateTransaction => {
  const base = {
    amount: fromCents(transactionCents),
    postedOn: toPostedOnDate(order.orderDate),
  };

  if (items.length === 1) {
    const [row] = items;
    return { ...base, coa: row.coa, ...(row.name ? { memo: row.name } : {}), split: null };
  }

  // A split transaction carries no parent category; the rows own the categorisation.
  return { ...base, coa: null, split: { items } };
};

export const buildOrderCandidate = (
  order: MerchantOrder,
  options: BuildOrderCandidateOptions = {},
): BuildOrderCandidateResult => {
  if (!isClassifiedMerchantOrder(order)) {
    return { ok: false, skipReason: merchantOrderSkipReason.notClassified };
  }

  const grandTotalCents = asCents(order.grandTotal);
  if (!grandTotalCents) {
    return { ok: false, skipReason: merchantOrderSkipReason.noTotal };
  }

  const transactionCents = toTransactionCents(grandTotalCents);
  const items = buildOrderSplitItems(order, transactionCents, options);

  if (items.length === 0) {
    return { ok: false, skipReason: merchantOrderSkipReason.noSplitRows };
  }

  const accountHint = accountHintFor(order);

  return {
    ok: true,
    candidate: {
      order,
      transaction: toCandidateTransaction(order, transactionCents, items),
      ...(accountHint ? { accountHint } : {}),
    },
  };
};
